import {useTheme} from "@emotion/react";
import {useLoaderData} from "@remix-run/react";
import {useEffect, useState} from "react";
import {Motion, spring} from "react-motion";
import threeVerticalDots from "../../assets/icons/socialNetworks/threeVerticalDots.svg";
import x from "../../assets/icons/socialNetworks/x.svg";
import {
  initialFontSize,
  maxStep,
  stepSize,
  useFontSize,
} from "../../context/fontSizeChange.jsx";
import CTA from "../../elements/CTA";
import IncreaseFontSize from "../../elements/svgicon/IncreaseFontSize.jsx";
import ReduceFontSize from "../../elements/svgicon/ReduceFontSize.jsx";
import ShareIcon from "../../elements/svgicon/ShareIcon.jsx";
import XIcon from "../../elements/svgicon/Xicon.jsx";
import {Text} from "../../elements/text";
import useHasHydrated from "../../hooks/useHasHydrated";
import {theme} from "../../styles/theme.js";
import {decodeHtmlEntities} from "../../utils/text.js";
import {fadeIn, slideIn} from "../Header/styles.js";
import SectionWrapper from "../SectionWrapper";
import ShareSocialMediaLinks from "../ShareSocialMediaLinks";
import {
  BlueUnderline,
  HeaderInfoWrapper,
  Icon,
  IconWrapper,
  NavigationWrapper,
  RightContentWrapper,
} from "./styles.js";

export default function StickyBar({showHeader, showFixedHeader, headerTitle}) {
  const hasHydrated = useHasHydrated();
  const {sections} = useLoaderData();
  const target = sections?.acf?.right_side?.cta?.target;
  const title = decodeHtmlEntities(sections?.acf?.right_side?.cta?.title);
  const url = sections?.acf?.right_side?.cta?.url;
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  const {breakpoints} = useTheme();
  const {isMobile} = breakpoints;
  const {increaseFontSize, reduceFontSize, fontSize} = useFontSize();
  const [scrollPercentage, setScrollPercentage] = useState(0);

  useEffect(() => {
    if (!hasHydrated) return;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const availableScroll = documentHeight - windowHeight;
      const percentageScrolled = (scrollTop / availableScroll) * 100;

      setScrollPercentage(percentageScrolled);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasHydrated]);

  /*
  const [copySuccess, setCopySuccess] = useState(false);

  const copyToClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);

      // Hide the success message after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 200);
    } catch (err) {
      setCopySuccess("Failed to copy!");
    }
  };
 */

  if (!showFixedHeader) return;

  return (
    <SectionWrapper
      styles={{
        zIndex: 5,
        width: "100%",
        boxShadow: "0px 10px 16px 0px #1D31400A",
        position: "fixed",
        top: showHeader ? (isMobile ? 91 : 95) : 0,
        left: 0,
        overflow: "visible",
        ...(showFixedHeader && {
          animation: `${slideIn} 0.3s ease, ${fadeIn} 0.3s ease`,
          transition: "all 0.3s ease",
        }),
      }}
      backgroundColor="white"
      innerWrapperStyles={{
        position: "initial !important",
        paddingInline: isMobile ? 10 : 64,
        overflow: "visible",
      }}
    >
      <NavigationWrapper showSocialLinks={showSocialLinks}>
        <HeaderInfoWrapper>
          <Text type="stickyHeader">{headerTitle}</Text>
          {isMobile ? (
            <>
              <IconWrapper onClick={() => setShowSocialLinks(!showSocialLinks)}>
                <Icon src={showSocialLinks ? x : threeVerticalDots} />
              </IconWrapper>
              {showSocialLinks && <ShareSocialMediaLinks align="vertical" />}
            </>
          ) : (
            <RightContentWrapper>
              {/*hidde for now */}
              {/*<IconWrapper>*/}
              {/*    <Icon*/}
              {/*        src={like}*/}
              {/*        width={showSocialLinks ? 20 : 24}*/}
              {/*        height={showSocialLinks ? 20 : 24}*/}
              {/*    />*/}
              {/*</IconWrapper>*/}
              <Motion
                style={{
                  marginRightSocialIcons: spring(showSocialLinks ? 0 : -140, {
                    stiffness: 210,
                  }),
                  marginLeftTextIcon: spring(showSocialLinks ? 140 : 0, {
                    stiffness: 210,
                  }),
                }}
              >
                {(style) => (
                  <>
                    {showSocialLinks ? (
                      <ShareSocialMediaLinks
                        styles={{marginRight: style.marginRightSocialIcons}}
                      />
                    ) : (
                      <>
                        <IconWrapper
                          styles={{
                            marginRight: style.marginRightTextIcon,
                            cursor: fontSize === initialFontSize ? "default" : "pointer",
                          }}
                          onClick={reduceFontSize}
                        >
                          <ReduceFontSize
                            width={20}
                            height={20}
                            color={fontSize === initialFontSize ? "#737373" : "#27509B"}
                          />
                        </IconWrapper>
                        <IconWrapper
                          styles={{
                            marginRight: style.marginRightTextIcon,
                            cursor:
                              fontSize === initialFontSize + stepSize * maxStep
                                ? "default"
                                : "pointer",
                          }}
                          onClick={increaseFontSize}
                        >
                          <IncreaseFontSize
                            color={
                              fontSize === initialFontSize + stepSize * maxStep
                                ? "#737373"
                                : "#27509B"
                            }
                            width={20}
                            height={20}
                          />
                        </IconWrapper>
                      </>
                    )}
                  </>
                )}
              </Motion>
              <IconWrapper onClick={() => setShowSocialLinks(!showSocialLinks)}>
                {showSocialLinks ? (
                  <XIcon width={22} height={22} className="icon" />
                ) : (
                  <ShareIcon width={24} height={24} />
                )}
              </IconWrapper>
              {title && (
                <CTA
                  variant={"viaMichelin"}
                  type="primary"
                  buttonColor={theme?.colors?.brandLightBlue}
                  size={"small"}
                  to={url}
                  target={target}
                >
                  {title}
                </CTA>
              )}
            </RightContentWrapper>
          )}
        </HeaderInfoWrapper>
        <BlueUnderline
          width={`${scrollPercentage}%`}
          styles={isMobile && showSocialLinks ? {top: 52} : null}
        />
      </NavigationWrapper>
    </SectionWrapper>
  );
}
